import * as React from 'react';
import { produce } from 'immer';
import { PlusOutlined, EllipsisOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import apptracker from '@utils/sensor/apptracker';
import * as Events from '@src/utils/sensor/track';
import { DEFAULT_RULE_OPERATOR } from '../../constant';
import { useClickOutsideWithPopup } from '../../utils';
import * as Store from '../store';
import SavedFilterSelector from './SavedFilterSelector';
import DimensionSelector from './DimensionSelector';
import * as Styled from './styled';
import type { FilterValue } from '../../../types';

const DEFAULT_ACTION_OPTIONS = [
  {
    label: 'in Saved Filters',
    value: 'filter' as const,
  },
  {
    label: 'in Dimensions',
    value: 'dimension' as const,
  },
];

const selectorStyle = { maxWidth: 'calc(100% - 140px)', flex: 1 };

const AppendedAtom = () => {
  const [idle, setIdle] = React.useState(true);
  const [status, setStatus] = Store.useAppendedAtomStatus();
  const metaMap = Store.useFilterMetaMap();
  const [filterStatus, setFilterStatus] = Store.useFilterStatus();
  const [, setFilterValue] = Store.useFilterValue();
  const disabled = Store.useFilterDisabled();
  const onChange = Store.useOnChange();

  const [containerRef, outsidePopupClassName] = useClickOutsideWithPopup(
    React.useCallback(() => setIdle(true), []),
  );

  if (idle) {
    return (
      <Styled.AtomContainer
        $disabled={disabled || ['editing-advanced', 'simple-saving'].includes(filterStatus)}
        onMouseDown={() => requestAnimationFrame(() => setIdle(false))}
      >
        <PlusOutlined />
        <EllipsisOutlined />
      </Styled.AtomContainer>
    );
  }

  const onApplyFilter = (value: FilterValue, type: string) => {
    setIdle(true);
    setFilterStatus('idle');
    requestAnimationFrame(() => onChange?.(value));

    const payload = {
      type,
      filterId: value.fid,
    };

    if (payload.filterId && payload.filterId < 0) {
      payload.filterId = undefined;
    }

    apptracker(Events.APPLY_SAVED_FILTER_ON_FILTER_BUILDER, payload);
  };
  const onApplyField = (value: string, tag = false) => {
    setIdle(true);

    // Append a new empty dimension rule atom
    requestAnimationFrame(() => {
      setFilterValue((prev) => ({
        rule: produce(prev.rule, (draft) => {
          draft.push([
            {
              field: tag ? 'TAG' : value,
              key: tag ? value : null,
              op: (!tag && metaMap.get(value)!.operators[0]?.value) || DEFAULT_RULE_OPERATOR,
              value: '',
              display: '',
            },
          ]);
        }),
      }));
      setFilterStatus('editing');
    });

    apptracker(
      tag
        ? Events.FILTER_BUILDER_APPLY_CUSTOM_TAG_ON_SIMPLE_MODE
        : Events.FILTER_BUILDER_APPLY_DIMENSION_ON_SIMPLE_MODE,
      { field: value },
    );
  };

  const setActionType = (value: 'filter' | 'dimension') => {
    setStatus(value);
    apptracker(Events.FILTER_BUILDER_APPLY_ACTION_TYPE, {
      actionType: value,
    });
  };

  return (
    <div ref={containerRef} style={{ borderRadius: 6, maxWidth: '100%' }}>
      <Styled.AtomSpaceCompact style={{ width: 400 }}>
        {status === 'filter' && (
          <SavedFilterSelector
            classNames={{ popup: { root: outsidePopupClassName } }}
            style={selectorStyle}
            onSelect={(value, option) => onApplyFilter(option.filterValue, option.type)}
          />
        )}
        {status === 'dimension' && (
          <DimensionSelector style={selectorStyle} onSelect={(value) => onApplyField(value)} />
        )}
        <Select
          value={status}
          options={DEFAULT_ACTION_OPTIONS}
          virtual={false}
          classNames={{ popup: { root: outsidePopupClassName } }}
          style={{ width: 140 }}
          onChange={(value) => requestAnimationFrame(() => setActionType(value))}
        />
      </Styled.AtomSpaceCompact>
    </div>
  );
};

export default React.memo(AppendedAtom);
